import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { parsePlanSegments } from "@/constants/monitoring";
import { DashboardCard } from "../Dashboard/DashboardCard";
import { useNavigate } from "@tanstack/react-router";
import { getClientOverview } from "../../api/client.api";
import { useQueryClient } from "@tanstack/react-query";

interface ClientCardProps {
  name: string;
  email: string;
  plan: string;
  subscriptionEndDate: string;
  className?: string;
  clientId?: string;
  showCheckbox?: boolean;
  isSelected?: boolean;
  onSelect?: () => void;
}

export function ClientCard({
  name,
  email,
  plan,
  subscriptionEndDate,
  className,
  clientId,
  showCheckbox,
  isSelected,
  onSelect,
}: ClientCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleClientClick = () => {
    navigate({
      to: `/admin/client/${clientId || name}`,
      search: { clientName: name },
    });
  };

  const handleMouseEnter = () => {
    if (!clientId) return;

    queryClient.prefetchQuery({
      queryKey: ["client-overview", clientId],
      queryFn: () => getClientOverview(clientId),
      staleTime: 5 * 60 * 1000,
    });
  };

  const planSegments = parsePlanSegments(plan);

  return (
    <DashboardCard
      className={cn(className, "flex h-[230px] flex-col")}
      onClick={handleClientClick}
    >
      <div className="flex flex-row gap-2" onMouseEnter={handleMouseEnter}>
        {showCheckbox && (
          <div
            className="flex shrink-0 items-start pt-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={isSelected}
              onCheckedChange={onSelect}
              className="border-white"
            />
          </div>
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h1 className="text-[20px] leading-6 font-bold -tracking-wide">
            {name}
          </h1>
          <p className="text-medium font-xs text-black-200 block truncate leading-normal">
            {email}
          </p>
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {planSegments.length > 0 ? (
          <div className="flex min-w-0 flex-wrap gap-1.5">
            {planSegments.map((segment, i) => (
              <Badge
                key={`${segment.label}-${segment.icon}-${i}`}
                variant="plan"
                className="bg-brand-500 max-w-[160px] shrink-0 py-2.5! font-medium"
                title={segment.label}
              >
                <i
                  className={cn(segment.icon, "shrink-0 text-sm font-normal")}
                />
                <span className="block min-w-0 truncate text-sm">
                  {segment.label}
                </span>
              </Badge>
            ))}
          </div>
        ) : (
          <Badge variant="plan">{plan?.trim() || "—"}</Badge>
        )}
        <div className="mt-auto space-y-1">
          <h1 className="text-black-200 text-xs leading-3 font-medium -tracking-wide">
            Subscription Active until:
          </h1>
          <p className="text-black-50 text-xs leading-3 font-bold">
            {subscriptionEndDate}
          </p>
        </div>
      </div>
    </DashboardCard>
  );
}
